/**
 * One-shot breadcrumbs for album open → route → fetch → render.
 * Always captured in the support diagnostic snapshot (fleet + ticket logs).
 */

import { diagnosticLog } from './diagnosticLogger';

export type AlbumNavPhase =
  | 'click'
  | 'route'
  | 'fetch_start'
  | 'fetch_ok'
  | 'fetch_error'
  | 'render';

const flowStarts = new Map<string, number>();

function flowKey(albumId: string, source?: string): string {
  return `${source || 'unknown'}::${albumId}`;
}

export function logAlbumNavBreadcrumb(
  phase: AlbumNavPhase,
  albumId: string,
  details?: Record<string, unknown>,
): void {
  try {
    const id = String(albumId || '').trim() || '(missing-id)';
    const source = typeof details?.source === 'string' ? details.source : undefined;
    const key = flowKey(id, source);
    const now = Date.now();

    if (phase === 'click') {
      flowStarts.set(key, now);
    }

    const startedAt = flowStarts.get(key);
    const elapsedMs = startedAt != null ? now - startedAt : undefined;

    diagnosticLog('info', 'navigation', `album-open:${phase}`, {
      album_id: id,
      source: source ?? null,
      phase,
      elapsed_ms: elapsedMs ?? null,
      ...details,
    });

    if (phase === 'render' || phase === 'fetch_error') {
      flowStarts.delete(key);
    }
  } catch {
    /* never propagate */
  }
}

export function logAlbumNavFailure(
  albumId: string,
  reason: string,
  details?: Record<string, unknown>,
): void {
  try {
    const id = String(albumId || '').trim() || '(missing-id)';
    const source = typeof details?.source === 'string' ? details.source : undefined;
    const key = flowKey(id, source);
    const startedAt = flowStarts.get(key);
    const elapsedMs = startedAt != null ? Date.now() - startedAt : undefined;

    diagnosticLog('error', 'navigation', `album-open failed: ${reason}`, {
      album_id: id,
      source: source ?? null,
      phase: 'fetch_error',
      elapsed_ms: elapsedMs ?? null,
      ...details,
    });
    flowStarts.delete(key);
  } catch {
    /* never propagate */
  }
}
